import apiService from './ApiService';

class MateriaService {
  async getMaterias(options = {}) {
    const params = new URLSearchParams();
    if (options.idColegio) params.append('idColegio', options.idColegio);
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    if (options.cursoFiltro) params.append('cursoFiltro', options.cursoFiltro);
    const qs = params.toString();
    const response = await apiService.get(`/materias${qs ? `?${qs}` : ''}`);
    const materias = response?.materias || response?.Materias || response?.data || response || [];
    const items = Array.isArray(materias) ? materias : [];
    const total = response?.total ?? response?.Total ?? items.length;
    const totalSinCursosAsociados = response?.totalSinCursosAsociados ?? response?.TotalSinCursosAsociados ?? 0;
    if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
      return { items, total, totalSinCursosAsociados };
    }
    return items;
  }

  async getMateriasActivas(options = {}) {
    const params = new URLSearchParams();
    if (options.idColegio) params.append('idColegio', options.idColegio);
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    if (options.cursoFiltro) params.append('cursoFiltro', options.cursoFiltro);
    const qs = params.toString();
    const response = await apiService.get(`/materias/activos${qs ? `?${qs}` : ''}`);
    const materias = response?.materias || response?.Materias || response?.data || response || [];
    const items = Array.isArray(materias) ? materias : [];
    const total = response?.total ?? response?.Total ?? items.length;
    const totalSinCursosAsociados = response?.totalSinCursosAsociados ?? response?.TotalSinCursosAsociados ?? 0;
    if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
      return { items, total, totalSinCursosAsociados };
    }
    return items;
  }

  async getMisMaterias(options = {}) {
    const params = new URLSearchParams();
    if (options.idColegio) params.append('idColegio', options.idColegio);
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    const qs = params.toString();
    const response = await apiService.get(`/materias/mis${qs ? `?${qs}` : ''}`);
    const materias = response?.materias || response?.Materias || response?.data || response || [];
    const items = Array.isArray(materias) ? materias : [];
    const total = response?.total ?? response?.Total ?? items.length;
    const totalSinCursosAsociados = response?.totalSinCursosAsociados ?? response?.TotalSinCursosAsociados ?? 0;
    if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
      return { items, total, totalSinCursosAsociados };
    }
    return items;
  }

  async getMateriasPublicasPorAnio(colegioId) {
    if (!colegioId) {
      throw new Error('No se encontró el colegio.');
    }
    const response = await apiService.getPublic(`/materias/publicas/por-anio?colegioId=${encodeURIComponent(colegioId)}`);
    const anios = response?.anios || response?.Anios || response?.data || response || [];
    return Array.isArray(anios) ? anios : [];
  }

  async getMateriasPublicasPorAnioConAsistentes(colegioId) {
    if (!colegioId) {
      throw new Error('No se encontró el colegio.');
    }
    const response = await apiService.getPublic(`/materias/publicas/por-anio-con-asistentes?colegioId=${encodeURIComponent(colegioId)}`);
    const anios = response?.anios || response?.Anios || response?.data || response || [];
    return Array.isArray(anios) ? anios : [];
  }

  async getAsistentesPublicosPorMateria(materiaId, colegioId) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    if (!colegioId) {
      throw new Error('No se encontró el colegio.');
    }
    const response = await apiService.getPublic(`/materias/publicas/${materiaId}/asistentes?colegioId=${encodeURIComponent(colegioId)}`);
    const asistentes = response?.asistentes || response?.Asistentes || response?.data || response || [];
    return Array.isArray(asistentes) ? asistentes : [];
  }

  async validateAsistentePassword(idOpenAIAssistant, colegioId, password) {
    if (!idOpenAIAssistant) {
      throw new Error('No se encontró el asistente.');
    }
    if (!colegioId) {
      throw new Error('No se encontró el colegio.');
    }
    const response = await apiService.postPublic('/Asistente/publico/validar-password', {
      idOpenAIAssistant,
      colegioId,
      password
    });
    return response;
  }

  async getMateriaById(id) {
    const response = await apiService.get(`/materias/${id}`);
    const list = response?.materias || response?.Materias || [];
    return Array.isArray(list) && list.length > 0 ? list[0] : response;
  }

  async createMateria(payload) {
    const response = await apiService.post('/materias', payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos crear la materia.';
      throw new Error(msg);
    }
    return response;
  }

  async updateMateria(id, payload) {
    const response = await apiService.put(`/materias/${id}`, payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos actualizar la materia.';
      throw new Error(msg);
    }
    return response;
  }

  async updateMateriaActivo(id, activo) {
    const response = await apiService.put(`/materias/${id}/activo`, { activo });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado de la materia.';
      throw new Error(msg);
    }
    return response;
  }

  async deleteMateria(id) {
    return apiService.delete(`/materias/${id}`);
  }

  async deleteMateriasNoAsociadas(idColegio) {
    if (!idColegio) {
      throw new Error('No se encontró el colegio activo.');
    }

    const response = await apiService.post('/materias/eliminar-no-asociadas', { idColegio });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos eliminar las materias no asociadas.';
      throw new Error(msg);
    }

    return response;
  }

  async getProfesoresPorMateria(materiaId) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    const response = await apiService.get(`/materias/${materiaId}/profesores`);
    const profesores = response?.profesores || response?.Profesores || response?.data || response || [];
    return Array.isArray(profesores) ? profesores : [];
  }

  async assignProfesores(materiaId, profesorIds) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    const ids = Array.isArray(profesorIds) ? profesorIds : [];
    const response = await apiService.post(`/materias/${materiaId}/profesores/asociar`, {
      profesorIds: ids
    });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos asociar los profesores.';
      throw new Error(msg);
    }
    return response;
  }

  async removeProfesores(materiaId, profesorIds) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    const ids = Array.isArray(profesorIds) ? profesorIds : [];
    const response = await apiService.post(`/materias/${materiaId}/profesores/desasociar`, {
      profesorIds: ids
    });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos desasociar los profesores.';
      throw new Error(msg);
    }
    return response;
  }

  async getCursosPorMateria(materiaId) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    const response = await apiService.get(`/materias/${materiaId}/cursos`);
    const cursos = response?.cursos || response?.Cursos || response?.data || response || [];
    return Array.isArray(cursos) ? cursos : [];
  }

  async assignCursos(materiaId, cursoIds) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    const ids = Array.isArray(cursoIds) ? cursoIds : [];
    const response = await apiService.post(`/materias/${materiaId}/cursos/asociar`, {
      cursoIds: ids
    });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos asociar los cursos.';
      throw new Error(msg);
    }
    return response;
  }

  async removeCursos(materiaId, cursoIds) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    const ids = Array.isArray(cursoIds) ? cursoIds : [];
    const response = await apiService.post(`/materias/${materiaId}/cursos/desasociar`, {
      cursoIds: ids
    });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos desasociar los cursos.';
      throw new Error(msg);
    }
    return response;
  }

  async getAnios() {
    const response = await apiService.get('/materias/anios');
    const anios = response?.anios || response?.Anios || response?.data || response || [];
    return Array.isArray(anios) ? anios : [];
  }

  async getAniosActivos() {
    const response = await apiService.get('/materias/anios/activos');
    const anios = response?.anios || response?.Anios || response?.data || response || [];
    return Array.isArray(anios) ? anios : [];
  }

  async getAnioById(id) {
    const response = await apiService.get(`/materias/anios/${id}`);
    const list = response?.anios || response?.Anios || [];
    return Array.isArray(list) && list.length > 0 ? list[0] : response;
  }

  async updateAnio(id, valor) {
    const response = await apiService.put(`/materias/anios/${id}`, { valor });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos actualizar el año.';
      throw new Error(msg);
    }
    return response;
  }

  async updateAnioActivo(id, activo) {
    const response = await apiService.put(`/materias/anios/${id}/activo`, { activo });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos actualizar el año.';
      throw new Error(msg);
    }
    return response;
  }

  async getUnidades(materiaId, options = {}) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    const params = new URLSearchParams({ materiaId });
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    const qs = params.toString();
    const response = await apiService.get(`/unidades${qs ? `?${qs}` : ''}`);
    const unidades = response?.unidades || response?.Unidades || response?.data || response || [];
    const items = Array.isArray(unidades) ? unidades : [];
    const total = response?.total ?? response?.Total ?? items.length;
    if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
      return { items, total };
    }
    return items;
  }

  async getUnidadesActivas(materiaId, options = {}) {
    if (!materiaId) {
      throw new Error('No se encontró la materia seleccionada.');
    }
    const params = new URLSearchParams({ materiaId });
    if (options.start !== undefined && options.start !== null) params.append('start', options.start);
    if (options.end !== undefined && options.end !== null) params.append('end', options.end);
    const qs = params.toString();
    const response = await apiService.get(`/unidades/activos${qs ? `?${qs}` : ''}`);
    const unidades = response?.unidades || response?.Unidades || response?.data || response || [];
    const items = Array.isArray(unidades) ? unidades : [];
    const total = response?.total ?? response?.Total ?? items.length;
    if (options.includeTotal || options.start !== undefined || options.end !== undefined) {
      return { items, total };
    }
    return items;
  }

  async createUnidad(payload) {
    const response = await apiService.post('/unidades', payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos crear la unidad.';
      throw new Error(msg);
    }
    return response;
  }

  async updateUnidad(id, payload) {
    const response = await apiService.put(`/unidades/${id}`, payload);
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos actualizar la unidad.';
      throw new Error(msg);
    }
    return response;
  }

  async updateUnidadActivo(id, activo) {
    const response = await apiService.put(`/unidades/${id}/activo`, { activo });
    if (response && typeof response.ok !== 'undefined' && !response.ok) {
      const msg = response.message || response.error || response.title || 'No pudimos actualizar el estado de la unidad.';
      throw new Error(msg);
    }
    return response;
  }

  async deleteUnidad(id) {
    return apiService.delete(`/unidades/${id}`);
  }
}

const materiaService = new MateriaService();

export default materiaService;
